import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { initialPath } from "~misc/paths";
import Select from "~components/Form/components/Select";
import MetricsCards from "../../components/MetricsCards";
import VisitsChart from "../../components/VisitsChart";
import ReportListings from "../../components/ReportListings";
import { reportListingTypes } from "../../components/reportListingTypes";
import useSalesForceData from "../../hooks/useSalesForceData";



const formatarDataLabel = (isoDate) => {
  const [y, m, d] = isoDate.substring(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

const toIso      = (d) => d.toISOString().substring(0, 10);
const toPercent  = (val) => Math.min((val ?? 0) * 100, 100);
const fmtDecimal = (val) => ((val ?? 0).toFixed(2)).replace(".", ",");
const sumInRange = (map, from, to) =>
  Object.entries(map)
    .filter(([d]) => d >= from && d <= to)
    .reduce((acc, [, v]) => acc + v, 0);

const porRazaoSocial = (a, b) => (a.razaoSocial ?? "").localeCompare(b.razaoSocial ?? "", "pt-BR");

const ordenacaoOptions = [
  { value: "maisErros",  label: "Pesquisas com mais erros", compare: (a, b) => b.qtdErros - a.qtdErros || porRazaoSocial(a, b) },
  { value: "alfabetica", label: "Ordem Alfabética",         compare: porRazaoSocial },
];

const erroPesquisaColumns = [
  {
    key: "cliente",
    label: "Cliente",
    render: (row) => (
      <Link to={`${initialPath}/pesquisa-de-preco/${row.clienteId}`} className="cursor-pointer hover:underline">
        {row.cliente}
      </Link>
    ),
  },
  { key: "dataPesquisa",  label: "Data da Pesquisa" },
  { key: "erros",         label: "Erros"            },
  { key: "errosCimentos", label: "Erros Cimentos"   },
  { key: "errosColantes", label: "Erros Colantes"   },
];


const SalesForceIndex = () => {
  const { sfItems, pesquisas, errosPorTipo, updatedAt, isRefreshing } = useSalesForceData();

  const sfCards = useMemo(
    () => sfItems.find((item) => item.cards)?.cards ?? {},
    [sfItems],
  );


  const hoje = useMemo(() => {
    const datas = sfItems
      .filter((item) => item.data)
      .map((item) => new Date(item.data))
      .filter((d) => !Number.isNaN(d.getTime()));
    if (!datas.length) return new Date();
    return new Date(Math.max(...datas.map((d) => d.getTime())));
  }, [sfItems]);

  const {
    chartLabels,
    visitasData,
    pesquisasData,
    pesquisasSemErroData,
    separatorIndex,
    cards,
  } = useMemo(() => {
    const todayStr      = toIso(hoje);
    const monthStartStr = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}-01`;
    const inicioJanelaStr = toIso(new Date(+hoje - 13 * 864e5));
    const weekStartStr  = toIso(new Date(+hoje + (hoje.getDay() === 0 ? -6 : 1 - hoje.getDay()) * 864e5));

    const isInWindow = (isoDate) => {
      const d = isoDate.substring(0, 10);
      return d >= inicioJanelaStr && d <= todayStr;
    };

    const visitasMap = sfItems
      .filter((item) => item.totalVisitasValidas !== undefined && isInWindow(item.data))
      .reduce((acc, item) => {
        const key = item.data.substring(0, 10);
        acc[key] = (acc[key] ?? 0) + item.totalVisitasValidas;
        return acc;
      }, {});

    const { pesquisasMap, pesquisasSemErroMap } = pesquisas
      .filter((p) => isInWindow(p.dataPesquisa))
      .reduce(
        (acc, p) => {
          const key = p.dataPesquisa.substring(0, 10);
          acc.pesquisasMap[key] = (acc.pesquisasMap[key] ?? 0) + 1;
          if (p.qtdErros === 0) {
            acc.pesquisasSemErroMap[key] = (acc.pesquisasSemErroMap[key] ?? 0) + 1;
          }
          return acc;
        },
        { pesquisasMap: {}, pesquisasSemErroMap: {} },
      );

    const allDates = [...new Set([...Object.keys(visitasMap), ...Object.keys(pesquisasMap)])]
      .filter((d) => (visitasMap[d] ?? 0) + (pesquisasMap[d] ?? 0) + (pesquisasSemErroMap[d] ?? 0) > 0)
      .sort();

    const semana = {
      visitas:   sumInRange(visitasMap,          weekStartStr, todayStr),
      pesquisas: sumInRange(pesquisasMap,        weekStartStr, todayStr),
      semErro:   sumInRange(pesquisasSemErroMap, weekStartStr, todayStr),
    };

    const mes = {
      visitas:   sumInRange(visitasMap,          monthStartStr, todayStr),
      pesquisas: sumInRange(pesquisasMap,        monthStartStr, todayStr),
      semErro:   sumInRange(pesquisasSemErroMap, monthStartStr, todayStr),
    };

    const mesAtual = hoje.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
    const mesAtualLabel = mesAtual.charAt(0).toUpperCase() + mesAtual.slice(1);

    return {
      chartLabels:          [...allDates.map(formatarDataLabel), "Semana Atual", "Mês Atual"],
      visitasData:          [...allDates.map((d) => visitasMap[d] ?? 0),          semana.visitas,   mes.visitas],
      pesquisasData:        [...allDates.map((d) => pesquisasMap[d] ?? 0),        semana.pesquisas, mes.pesquisas],
      pesquisasSemErroData: [...allDates.map((d) => pesquisasSemErroMap[d] ?? 0), semana.semErro,   mes.semErro],
      separatorIndex:       allDates.length,
      mesAtualLabel,
      cards: [
        { title: "Total de Clientes na Carteira:", value: `${sfCards.totalClientes ?? 0} clientes`, bg: "#034EA2" },
        { title: "Visitas Presenciais/D.U.:",      value: fmtDecimal(sfCards.visitasPresenciais) },
        { title: "Visitas Remotas/D.U.:",          value: fmtDecimal(sfCards.visitasRemotas)     },
      ],
    };
  }, [sfItems, pesquisas, sfCards, hoje]);

  const [ordenacao, setOrdenacao] = useState(ordenacaoOptions[0]);

  const erroPesquisaData = useMemo(() => ({
    [reportListingTypes.erroPesquisa]: {
      columns: erroPesquisaColumns,
      rows: pesquisas
        .filter((p) => p.qtdErros > 0)
        .sort(ordenacao.compare)
        .map((p) => ({
          id:            p.id,
          clienteId:     p.clienteId,
          cliente:       `${p.codigoCliente} - ${p.razaoSocial}`,
          dataPesquisa:  formatarDataLabel(p.dataPesquisa),
          erros:         String(p.qtdErros).padStart(2, "0"),
          errosCimentos: p.errosCimentos,
          errosColantes: p.errosColantes,
        })),
    },
  }), [pesquisas, ordenacao]);

  const mesAtualLabel = useMemo(() => {
    const s = hoje.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  }, [hoje]);

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">

      {isRefreshing && (
        <div className="container">
          <div className="w-[130px] flex mt-4 items-center gap-2 bg-[#01054C] rounded-md px-4 py-2 text-xs font-semibold text-[#fff] animate-pulse">
            <span className="rounded-full" />
            Atualizando...
          </div>
        </div>
      )}

        <section className="rounded-md shadow border-gray-200 bg-white p-6">
          <header>
            <h2 className="text-[clamp(1rem,1.8vw,1.3125rem)] font-bold leading-none text-[#0A2E73]">
              Sales Force em{" "}
              <span className="text-[#37A800]">{mesAtualLabel}</span>:
            </h2>
          </header>
          <div className="mt-6 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))] min-[800px]:[grid-template-columns:repeat(3,minmax(0,1fr))]">
            {cards.map((card) => (
              <article
                key={card.title}
                className="flex min-h-[100px] flex-col items-center justify-center rounded-md px-4 py-3 text-center text-white"
                style={{ backgroundColor: card.bg ?? "#050B5C" }}
              >
                <p className="mb-2 text-[clamp(0.75rem,1vw,0.875rem)] font-bold leading-tight">
                  {card.title}
                </p>
                <h3 className="break-words text-[clamp(1.375rem,2.4vw,1.5625rem)] font-bold leading-none">
                  {card.value}
                </h3>
              </article>
            ))}
          </div>
        </section>

        <MetricsCards
          aderencia={toPercent(sfCards.aderenciaRota)}
          cobertura={toPercent(sfCards.coberturaCarteira)}
          m300Aderencia={toPercent(sfCards.m300AderenciaRota)}
          m300Cobertura={toPercent(sfCards.m300CoberturaCarteira)}
          errosPorTipo={errosPorTipo}
        />

        <VisitsChart
          labels={chartLabels}
          visitasData={visitasData}
          pesquisasData={pesquisasData}
          pesquisasSemErroData={pesquisasSemErroData}
          separatorIndex={separatorIndex}
        />

        <div className="container flex justify-end">
          <div className="min-w-[260px]">
            <Select
              label="Ordenar por:"
              name="ordenacaoErroPesquisa"
              options={ordenacaoOptions}
              value={ordenacao}
              onChange={(opt) => setOrdenacao(opt ?? ordenacaoOptions[0])}
              placeholder="Selecione"
              spacingControl={true}
            />
          </div>
        </div>

        <ReportListings
          type={reportListingTypes.erroPesquisa}
          dataByType={erroPesquisaData}
          updatedAt={updatedAt}
          footerNote="Atualização uma vez ao dia com referência d-1"
        />
      </div>
    </div>
  );
};

export default SalesForceIndex;
